import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { UploadCloud, Loader2, Plus, Trash2, Star, Pencil, MonitorPlay, X, Gauge } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const RES = [
  { h: 1080, label: "1080p Full HD" },
  { h: 720, label: "720p HD" },
  { h: 480, label: "480p Standard" },
  { h: 360, label: "360p Data Saver" },
];

const EMPTY = { name: "", height: 720, video_bitrate: "2M", audio_bitrate: "128k", is_default: false };

export default function UploadProfiles() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try { setRows((await api.get("/upload-profiles")).data); }
    catch { toast.error("Failed to load profiles"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ name: p.name, height: p.height, video_bitrate: p.video_bitrate, audio_bitrate: p.audio_bitrate, is_default: p.is_default }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Profile name is required"); return; }
    setSaving(true);
    try {
      if (editing) { await api.put(`/admin/upload-profiles/${editing.id}`, form); toast.success("Profile updated"); }
      else { await api.post("/admin/upload-profiles", form); toast.success("Profile created"); }
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (p) => {
    setBusy(p.id);
    try { await api.delete(`/admin/upload-profiles/${p.id}`); setRows((x) => x.filter((y) => y.id !== p.id)); toast.success("Profile deleted"); }
    catch { toast.error("Delete failed"); }
    finally { setBusy(null); }
  };

  const makeDefault = async (p) => {
    setBusy(p.id);
    try { await api.put(`/admin/upload-profiles/${p.id}`, { ...p, is_default: true }); toast.success(`"${p.name}" is now the default`); load(); }
    catch { toast.error("Action failed"); }
    finally { setBusy(null); }
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1100px] mx-auto">
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Upload Profiles</h1>
          <p className="text-gray-400 mt-1.5 text-sm">Presets that control how uploaded videos are compressed (resolution &amp; bitrate). The default profile is applied automatically to every upload.</p>
        </div>
        <button data-testid="add-profile-btn" onClick={openNew} className="gold-gradient text-black font-semibold px-4 py-2.5 rounded-lg flex items-center gap-2 hover:brightness-110 transition-all active:scale-[0.98]">
          <Plus className="h-4 w-4" /> New profile
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-32"><Loader2 className="h-6 w-6 text-amber-400 animate-spin" /></div>
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {rows.map((p, i) => (
            <motion.div key={p.id} data-testid={`profile-card-${p.id}`} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.05, 0.3) }}
              className={"relative bg-card border rounded-2xl p-5 transition-all " + (p.is_default ? "border-amber-500/50 shadow-[0_0_30px_rgba(245,158,11,0.08)]" : "border-gray-800 hover:border-amber-500/30")}>
              {p.is_default && <span data-testid={`profile-default-${p.id}`} className="absolute top-4 right-4 inline-flex items-center gap-1 text-[10px] font-bold text-amber-400 bg-amber-500/15 border border-amber-500/30 px-2 py-0.5 rounded-full"><Star className="h-3 w-3" fill="currentColor" /> DEFAULT</span>}
              <div className="h-11 w-11 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center"><MonitorPlay className="h-5 w-5 text-amber-400" /></div>
              <p className="mt-3 font-semibold text-white">{p.name}</p>
              <div className="mt-3 space-y-1.5 text-sm text-gray-400">
                <p className="flex items-center justify-between"><span className="text-gray-500">Resolution</span><span className="text-white font-medium">{p.height}p</span></p>
                <p className="flex items-center justify-between"><span className="text-gray-500">Video bitrate</span><span className="text-white font-medium">{p.video_bitrate}</span></p>
                <p className="flex items-center justify-between"><span className="text-gray-500">Audio bitrate</span><span className="text-white font-medium">{p.audio_bitrate}</span></p>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-800/70 flex items-center gap-2">
                {busy === p.id ? <Loader2 className="h-4 w-4 animate-spin text-amber-400" /> : (
                  <>
                    {!p.is_default && <button data-testid={`profile-make-default-${p.id}`} onClick={() => makeDefault(p)} className="text-xs font-medium text-amber-400 hover:text-amber-300 inline-flex items-center gap-1"><Star className="h-3.5 w-3.5" /> Set default</button>}
                    <div className="ml-auto flex items-center gap-1">
                      <button data-testid={`profile-edit-${p.id}`} onClick={() => openEdit(p)} className="h-8 w-8 rounded-lg text-gray-500 hover:text-amber-400 hover:bg-amber-500/10 flex items-center justify-center"><Pencil className="h-3.5 w-3.5" /></button>
                      <button data-testid={`profile-delete-${p.id}`} onClick={() => remove(p)} className="h-8 w-8 rounded-lg text-gray-500 hover:text-red-400 hover:bg-red-500/10 flex items-center justify-center"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-gray-800 max-w-md">
          <DialogHeader><DialogTitle className="font-display text-lg flex items-center gap-2"><Gauge className="h-5 w-5 text-amber-400" /> {editing ? "Edit profile" : "New upload profile"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label className="text-gray-300 text-sm">Profile name</Label>
              <Input data-testid="profile-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Sunday Service 720p" className="bg-gray-950 border-gray-800 focus-visible:ring-amber-500/40 h-11" /></div>
            <div className="space-y-2"><Label className="text-gray-300 text-sm">Resolution</Label>
              <div className="grid grid-cols-2 gap-2">
                {RES.map((r) => (
                  <button key={r.h} data-testid={`profile-res-${r.h}`} onClick={() => setForm({ ...form, height: r.h })}
                    className={"px-3 h-11 rounded-lg text-sm border " + (form.height === r.h ? "bg-amber-500/15 text-amber-400 border-amber-500/40" : "bg-card text-gray-400 border-gray-800 hover:text-white")}>{r.label}</button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label className="text-gray-300 text-sm">Video bitrate</Label>
                <Input data-testid="profile-vbitrate" value={form.video_bitrate} onChange={(e) => setForm({ ...form, video_bitrate: e.target.value })} placeholder="2M" className="bg-gray-950 border-gray-800 focus-visible:ring-amber-500/40 h-11 font-mono text-sm" /></div>
              <div className="space-y-2"><Label className="text-gray-300 text-sm">Audio bitrate</Label>
                <Input data-testid="profile-abitrate" value={form.audio_bitrate} onChange={(e) => setForm({ ...form, audio_bitrate: e.target.value })} placeholder="128k" className="bg-gray-950 border-gray-800 focus-visible:ring-amber-500/40 h-11 font-mono text-sm" /></div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input data-testid="profile-is-default" type="checkbox" checked={form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} className="h-4 w-4 accent-amber-500" />
              <span className="text-sm text-gray-300">Make this the default profile for new uploads</span>
            </label>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white border border-gray-800 flex items-center gap-1"><X className="h-4 w-4" /> Cancel</button>
              <button data-testid="save-profile" onClick={save} disabled={saving} className="gold-gradient text-black font-semibold px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-60">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {editing ? "Save changes" : "Create profile"}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
